import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  Row, Col, Card, Typography, Button, Tag, Table, 
  Select, Skeleton, message
} from 'antd';
import AppHeader from '../../components/Header';
import company from '../../services/company';
import env from '../../env';
import './index.css';

const { Title, Text } = Typography;
const { Option } = Select;

const ComparePage = () => {
  const [selectedCompanies, setSelectedCompanies] = useState([]);

  // Fetch all companies using React Query
  const { data: allCompanies, isLoading, error } = useQuery({
    queryKey: ['companies', 'all'],
    queryFn: async () => {
      const response = await company.get('?all=true');
      return response.data.data;
    }
  });

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleCompanySelect = (value) => {
    if (selectedCompanies.length >= 3) {
      return;
    }
    const selectedCompany = allCompanies?.find(c => c.id === value);
    if (selectedCompany) {
      setSelectedCompanies([...selectedCompanies, selectedCompany]);
    }
  };

  const handleRemoveCompany = (companyId) => {
    setSelectedCompanies(selectedCompanies.filter(c => c.id !== companyId));
  };

  const features = [
    { key: 'name', label: 'Company Name' },
    { key: 'line_of_business', label: 'Line of Business' },
    { key: 'contact', label: 'Contact Person' },
    { key: 'phone', label: 'Phone' },
    { key: 'website', label: 'Website' },
    { key: 'address', label: 'Address' },
    { key: 'commission', label: 'Commission' },
    { key: 'general_info', label: 'About' },
    { key: 'limits_of_coverage', label: 'Limits of Coverage' },
    { key: 'underwriting', label: 'Underwriting' },
    { key: 'states', label: 'States' },
    { key: 'files', label: 'Files' },
  ];

  const LoadingSkeleton = () => (
    <div style={{ width: "100%" }}>
      <Skeleton.Input active block style={{marginTop: 20, height: 40}} />
    </div>
  );

  if (error) {
    message.error('Failed to fetch companies');
  }

  return (
    <>
      <AppHeader />
      <div className="compare-container">
        <div className="compare-content">
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Card>
                <Title level={3}>Compare Companies</Title>
                <Text type="secondary">Select up to 3 companies to compare their features</Text>
                {isLoading ? (
                  <LoadingSkeleton />
                ) : (
                  <>
                    <div className="company-selector">
                      <Select
                        showSearch
                        placeholder="Select a company to compare"
                        style={{ width: '100%' }}
                        onChange={handleCompanySelect}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                        value={undefined}
                        loading={isLoading}
                        disabled={isLoading || selectedCompanies.length >= 3}
                      >
                        {allCompanies
                          ?.filter(c => !selectedCompanies.find(sc => sc.id === c.id))
                          .map(company => (
                            <Option key={company.id} value={company.id}>
                              {company.name}
                            </Option>
                          ))
                        }
                      </Select>
                    </div>

                    <div className="selected-companies">
                      <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
                        {selectedCompanies.map(company => (
                          <Col key={company.id} xs={24} sm={12} md={8}>
                            <div className="landing-company-card">
                              <div className="landing-company-card-content">
                                <div className='company-image-container' style={{padding: 16}}>
                                  <Row align="middle" gutter={16}>
                                    <Col span={4}>
                                      <img 
                                        src={`${env.BACKEND_STORAGE_URL}/${company.logo}`} 
                                        alt={company.name} 
                                        className="company-image"
                                        style={{ maxHeight: "50px"}}
                                      />
                                    </Col>
                                    <Col span={20}>
                                      <p className="landing-company-name" style={{fontSize: 16, fontWeight: 600, paddingLeft: 9}}>
                                        {company.name}
                                      </p>
                                    </Col>
                                  </Row>
                                </div>

                                <div className="landing-company-details" style={{marginTop: '-20px'}}>
                                  {/* States section */}
                                  {company.states && company.states.length > 0 && (
                                    <div className="company-tags">
                                      {company.states.slice(0, 10).map(state => (
                                        <Tag color="blue" key={state.id}>
                                          {state.name}
                                        </Tag>
                                      ))}
                                      {company.states.length > 10 && 
                                        <Tag color="gray" style={{padding: 0, margin: 0, backgroundColor: '#fff', color: '#000', fontWeight: 600}}>
                                          +{company.states.length - 10} more
                                        </Tag>
                                      }
                                    </div>
                                  )}

                                  {/* Line of Business section */}
                                  {company.line_of_businesses && company.line_of_businesses.length > 0 && (
                                    <div className="company-tags" style={{ marginTop: '8px' }}>
                                      {company.line_of_businesses.slice(0, 10).map(lob => (
                                        <Tag color="green" key={lob.id}>
                                          {lob.name}
                                        </Tag>
                                      ))}
                                      {company.line_of_businesses.length > 10 && 
                                        <Tag color="gray" style={{ margin: 0, padding: 0, backgroundColor: '#fff', color: '#000', fontWeight: 600}}>
                                          +{company.line_of_businesses.length - 10} more
                                        </Tag>
                                      }
                                    </div>
                                  )}

                                  <Button 
                                    danger 
                                    onClick={() => handleRemoveCompany(company.id)}
                                    style={{ marginTop: '12px' }}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </>
                )}
              </Card>
            </Col>

            {selectedCompanies.length > 0 && (
              <Col span={24}>
                <Card>
                  <Table
                    dataSource={features}
                    pagination={false}
                    rowKey="key"
                    className="comparison-table"
                    size="small"
                    loading={isLoading}
                    scroll={{ x: 'max-content', y: 400 }}
                    sticky
                  >
                    <Table.Column 
                      title={<div style={{ fontWeight: 'bold' }}>Feature</div>} 
                      dataIndex="label" 
                      fixed="left"
                      width={150}
                      render={(text) => <div style={{ fontWeight: 'bold' }}>{text}</div>}
                    />
                    {selectedCompanies.map((company) => (
                      <Table.Column
                        key={company.id}
                        title={<div>{company.name}</div>}
                        render={(_, record) => (
                          <div
                            style={{ 
                              whiteSpace: 'pre-wrap', 
                              wordBreak: 'break-word',
                              minWidth: 200,
                              maxWidth: 300
                            }}
                            dangerouslySetInnerHTML={{ 
                              __html: Array.isArray(company[record.key]) 
                                ? record.key === 'files' 
                                  ? company[record.key].map(file => 
                                      `<a href="${env.BACKEND_STORAGE_URL}/${file.file_path}" target="_blank">${file.file_name || 'Document'}</a>`
                                    ).join('<br/>') 
                                  : company[record.key].map(item => item.name).join(', ') 
                                : company[record.key] || '-' 
                            }}
                          />
                        )}
                      />
                    ))}
                  </Table>
                </Card>
              </Col>
            )}
          </Row>
        </div>
      </div>
    </>
  );
};

export default ComparePage;